import React from 'react';
import { ComplianceReview, ReviewStatus, ComplianceVerdict } from './types';
import { TablePagination, useTablePagination } from '../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

interface Props {
  reviews: ComplianceReview[];
  onSelectReview: (review: ComplianceReview) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  departmentFilter: string;
  onDepartmentFilterChange: (value: string) => void;
  onCreateReview: () => void;
}

export const ComplianceReviewTable: React.FC<Props> = ({
  reviews,
  onSelectReview,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  departmentFilter,
  onDepartmentFilterChange,
  onCreateReview,
}) => {
  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(reviews);
  const revPage = useTablePagination(sortedItems, 25);

  const getStatusBadge = (status: ReviewStatus) => {
    switch (status) {
      case 'COMPLETED':
        return <span className="badge-pill badge-success">Completed</span>;
      case 'IN_PROGRESS':
        return <span className="badge-pill badge-warning">In Progress</span>;
      case 'SCHEDULED':
        return <span className="badge-pill badge-info">Scheduled</span>;
      case 'IN_REVIEW':
        return <span className="badge-pill badge-info">Report In Review</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed</span>;
      case 'DRAFT':
      default:
        return <span className="badge-pill badge-neutral">Draft</span>;
    }
  };

  const getVerdictBadge = (verdict?: ComplianceVerdict | null) => {
    switch (verdict) {
      case 'COMPLIANT':
        return <span className="badge-pill badge-success">Compliant</span>;
      case 'PARTIALLY_COMPLIANT':
        return <span className="badge-pill badge-warning">Partially Compliant</span>;
      case 'NON_COMPLIANT':
        return <span className="badge-pill badge-danger">Non-Compliant</span>;
      default:
        return <span style={{ color: '#94a3b8', fontSize: '12px' }}>-</span>;
    }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Controls Bar */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', flex: 1 }}>
          <input
            type="text"
            placeholder="Search review no, title, department or reviewer..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 12px', background: '#ffffff', width: '280px' }}
          />

          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff' }}
          >
            <option value="">All Review Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="IN_REVIEW">Report In Review</option>
            <option value="COMPLETED">Completed</option>
            <option value="CLOSED">Closed</option>
          </select>

          <select
            value={departmentFilter}
            onChange={(e) => onDepartmentFilterChange(e.target.value)}
            style={{ fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px', background: '#ffffff' }}
          >
            <option value="">All Target Departments</option>
            <option value="Flight Operations">Flight Operations</option>
            <option value="Safety & SMS">Safety & SMS</option>
            <option value="Quality & Compliance">Quality & Compliance</option>
            <option value="Ground Operations">Ground Operations</option>
          </select>
        </div>

        <button
          onClick={onCreateReview}
          className="btn-primary"
          style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span>+ New Compliance Review</span>
        </button>
      </div>

      {/* Table */}
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="review_number" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Review No
              </SortableTh>
              <SortableTh field="title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Title & Scope
              </SortableTh>
              <SortableTh field="review_type" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Method & Type
              </SortableTh>
              <SortableTh field="target_department" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Department
              </SortableTh>
              <SortableTh field="lead_reviewer_name" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Lead Reviewer
              </SortableTh>
              <SortableTh field="planned_start_date" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Planned Window
              </SortableTh>
              <SortableTh field="status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Status
              </SortableTh>
              <SortableTh field="compliance_verdict" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Compliance Verdict
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                  No compliance reviews found.
                </td>
              </tr>
            ) : (
              revPage.paginatedItems.map((rev) => (
                <tr
                  key={rev.id}
                  onClick={() => onSelectReview(rev)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontWeight: 600, color: '#0284c7', fontFamily: 'monospace' }}>
                    {rev.review_number}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{rev.title}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Requirements: {rev.requirements_count || 0} • Findings: {rev.findings_count}
                    </div>
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    <div>{rev.review_type}</div>
                    <div style={{ color: '#64748b', fontSize: '11px' }}>{rev.review_method}</div>
                  </td>
                  <td style={{ fontSize: '12px', color: '#1e293b', fontWeight: 500 }}>
                    {rev.target_department}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>{rev.lead_reviewer_name}</div>
                    {rev.independence_verified && (
                      <span className="badge-pill badge-success" style={{ fontSize: '10px', marginTop: '2px' }}>
                        ✓ Independence Verified
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>
                    {rev.scheduled_start} - {rev.scheduled_end}
                  </td>
                  <td>
                    {getStatusBadge(rev.status)}
                  </td>
                  <td>
                    {getVerdictBadge(rev.compliance_verdict)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectReview(rev);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                    >
                      Review →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={revPage.currentPage}
        totalPages={revPage.totalPages}
        pageSize={revPage.pageSize}
        totalItems={revPage.totalItems}
        onPageChange={revPage.setPage}
        onPageSizeChange={revPage.setPageSize}
        itemLabel="compliance reviews"
      />
    </div>
  );
};
